/**
 * Phone number validation utilities using libphonenumber-js
 * Handles UK phone numbers and international formats
 */

// Imported from `/max` rather than the package root: the root ships `min` metadata,
// which omits the type-detection patterns, so `getType()` returns undefined for most
// countries (see node_modules/libphonenumber-js/README.md — ".getType() only works
// when import from 'libphonenumber-js/max'"). `isUkMobile` needs the number type.
// `/max` is used in preference to `/mobile` because `/mobile` metadata is optimised
// for mobile numbers and can report non-mobile numbers as invalid, and this module
// still has to validate landlines correctly.
import { type CountryCode, parsePhoneNumber } from 'libphonenumber-js/max';

/**
 * Validates a phone number using libphonenumber-js
 *
 * @param phone - The phone number to validate
 * @param country - The country code (default: 'GB' for UK)
 * @returns true if valid, false otherwise
 *
 * @example
 * isValidPhoneNumber('+447400123456') // true
 * isValidPhoneNumber('07400 123 456', 'GB') // true
 * isValidPhoneNumber('abc123') // false
 */
export function isValidPhoneNumber(phone: string, country: CountryCode = 'GB'): boolean {
  if (!phone || typeof phone !== 'string') {
    return false;
  }

  // Trim whitespace
  const trimmed = phone.trim();
  if (trimmed.length === 0) {
    return false;
  }

  try {
    // Parse the phone number with the given country code as default
    const phoneNumber = parsePhoneNumber(trimmed, country);

    // Check if the phone number is valid
    return phoneNumber?.isValid() ?? false;
  } catch {
    // If parsing fails, the number is invalid
    return false;
  }
}

/**
 * Validation message for a field that must hold a UK mobile number (CWL-132).
 */
export const UK_MOBILE_VALIDATION_MSG = 'Please enter a valid UK mobile number';

/**
 * Validates that a value is a UK mobile number.
 *
 * Stricter than {@link isValidPhoneNumber}: the number must be on the +44 calling
 * code *and* allocated as a mobile. Landlines, personal numbers and mobiles from
 * other countries are all rejected. +44 covers Great Britain plus Guernsey, Jersey
 * and the Isle of Man, which is intentional — see the note in the body.
 *
 * Both `07911 123456` and `+44 7911 123456` are accepted — they are the same
 * number, and both normalise to 11 national digits, which is the length CWL-132
 * requires. Because the number type is checked, no explicit digit count is
 * needed: an 11-digit non-mobile such as `07000000000` is still rejected.
 *
 * @param phone - The phone number to validate
 * @returns true if the value is a UK mobile number, false otherwise
 *
 * @example
 * isUkMobile('07911123456')   // true
 * isUkMobile('+447911123456') // true
 * isUkMobile('0772232932')    // false — 10 digits
 * isUkMobile('02071234567')   // false — landline
 */
export function isUkMobile(phone: string): boolean {
  if (!phone || typeof phone !== 'string') {
    return false;
  }

  const trimmed = phone.trim();
  if (trimmed.length === 0) {
    return false;
  }

  try {
    const phoneNumber = parsePhoneNumber(trimmed, 'GB');

    if (!phoneNumber?.isValid()) {
      return false;
    }

    // Checked on the +44 calling code, NOT `country === 'GB'`. Great Britain,
    // Guernsey, Jersey and the Isle of Man all share +44, and libphonenumber
    // resolves to the most specific one — `07911 123456` parses as country 'GG'.
    // Testing for 'GB' would therefore reject ordinary-looking UK mobiles.
    // A French mobile is also typed MOBILE, so the calling code still has to be
    // checked alongside the number type.
    return phoneNumber.countryCallingCode === '44' && phoneNumber.getType() === 'MOBILE';
  } catch {
    return false;
  }
}

/**
 * Formats a phone number to international format (E.164)
 *
 * @param phone - The phone number to format
 * @param country - The country code (default: 'GB' for UK)
 * @returns Formatted phone number in E.164 format (+441234567890) or empty string if invalid
 *
 * @example
 * formatPhoneNumber('07400 123 456') // '+447400123456'
 * formatPhoneNumber('+44 74 00 12 34 56') // '+447400123456'
 * formatPhoneNumber('invalid') // ''
 */
export function formatPhoneNumber(phone: string, country: CountryCode = 'GB'): string {
  if (!phone || typeof phone !== 'string') {
    return '';
  }

  try {
    const phoneNumber = parsePhoneNumber(phone.trim(), country);

    if (phoneNumber?.isValid()) {
      // Return in E.164 format (international format)
      return phoneNumber.number;
    }

    return '';
  } catch {
    return '';
  }
}

/**
 * Formats a phone number to national format (human-readable)
 *
 * @param phone - The phone number to format
 * @param country - The country code (default: 'GB' for UK)
 * @returns Formatted phone number in national format (07400 123456) or empty string if invalid
 *
 * @example
 * formatPhoneNumberNational('+447400123456') // '07400 123456'
 * formatPhoneNumberNational('07400123456') // '07400 123456'
 * formatPhoneNumberNational('invalid') // ''
 */
export function formatPhoneNumberNational(phone: string, country: CountryCode = 'GB'): string {
  if (!phone || typeof phone !== 'string') {
    return '';
  }

  try {
    const phoneNumber = parsePhoneNumber(phone.trim(), country);

    if (phoneNumber?.isValid()) {
      // Return in national format (e.g., "07400 123456")
      return phoneNumber.formatNational();
    }

    return '';
  } catch {
    return '';
  }
}

/**
 * Get validation error message for invalid phone number
 *
 * @param phone - The phone number to validate
 * @param country - The country code (default: 'GB' for UK)
 * @returns Error message string or null if valid
 *
 * @example
 * getPhoneValidationError('abc123') // 'Please enter a valid phone number'
 * getPhoneValidationError('07400123456') // null
 */
export function getPhoneValidationError(phone: string, country: CountryCode = 'GB'): string | null {
  if (!phone || typeof phone !== 'string' || phone.trim().length === 0) {
    return null; // Empty is handled by required validation
  }

  if (!isValidPhoneNumber(phone, country)) {
    return 'Please enter a valid phone number (e.g., +44 7911 123456 or 07911 123456)';
  }

  return null;
}
